/**
 * Created Sprint: hotfix-sw-update-white-screen (FCE-SWUPDATE-INITEVAL-001)
 * Last contract-affecting Sprint: hotfix-sw-update-white-screen — FCE-SWUPDATE-INITEVAL-001
 *   edge-aware hardening: the one-shot "unregister SW + reload" self-heal became a
 *   BOUNDED backoff (Owner 2026-07-02 §1.8, options b+c). Synthetic verification
 *   (Test B) proved one-shot immediate reload does NOT recover a returning user when
 *   the fault is PERSISTENT per reload — the shape of a Cloudflare edge/zone cache
 *   propagation window (the suspected true root cause; the SW-precache serves a
 *   complete chunk set so the SW is not the sole cause). New behavior: on a
 *   chunk-load failure, retry via a PLAIN reload after ~3s/6s/10s WITHOUT
 *   unregistering (preserve the precache — under an edge root cause the SW cache may
 *   still be serving other chunks correctly, and killing it only widens edge
 *   exposure); only after those plain retries are exhausted, unregister the SW +
 *   reload as a last resort (covers the SW-defect root cause); after that, give up
 *   and let the failure surface (bounded — no reload storm). Still cleared after a
 *   confirmed-clean mount so a FUTURE deploy's legit failure heals fresh.
 * Freeze status: not_frozen
 * Purpose: Zero-dependency, bounded self-heal for the "returning user hits a
 *   text/html-for-chunk dynamic-import failure after a new deploy → white screen"
 *   case (OBS-156), robust to a short edge-cache propagation window.
 *
 *   DELIBERATELY import-free (no React, no other app modules) so it is bundled into
 *   the ENTRY chunk and runs even when the lazy chunks (swLifecycle / App / their
 *   deps) fail to load — avoiding the chicken-and-egg where the recovery code lives
 *   in the very chunk that failed. It is the user-facing lifeline that lets a
 *   returning user survive the "defective old SW / stale edge → fixed version" jump
 *   (handoff §1 / §2.2 + evidence/selfheal-synthetic-verification.md §4).
 *
 * Related Docs: docs/plans/hotfix-sw-update-white-screen/handoff.md §2.2;
 *   evidence/selfheal-synthetic-verification.md §4 (edge-converge evaluation);
 *   backlog OBS-156; Vite vite:preloadError (Context7); hard-rules §4 / §11.2.
 */

/**
 * SessionStorage attempt-counter key. sessionStorage (not localStorage) so the
 * counter is scoped to the tab/episode and never persists across a genuinely-fresh
 * visit. Holds the number of recovery reloads already issued this episode.
 */
const ATTEMPTS_KEY = "kb-sw-chunk-recovery-attempts";

/**
 * Plain-reload retry backoff (ms). Each entry is a retry that reloads WITHOUT
 * unregistering the Service Worker (preserve precache / option c) — giving a short
 * edge-cache propagation window time to converge. After these are exhausted, one
 * last-resort unregister-and-reload runs, then recovery gives up (bounded).
 */
const BACKOFF_MS = [3_000, 6_000, 10_000];

/**
 * Total bounded reloads before giving up: the plain-reload retries + one final
 * unregister-and-reload. Beyond this, the failure is allowed to surface (no storm).
 */
const MAX_ATTEMPTS = BACKOFF_MS.length + 1;

/** What a self-heal call decided to do (for tests / telemetry). */
export type SelfHealAction =
  | "retry" // plain reload after backoff (SW/precache preserved)
  | "unregister-reload" // last resort: unregister SW then reload
  | "give-up" // bounded retries exhausted → surface the failure (no storm)
  | "already-acted"; // per-load dedupe: another path already scheduled recovery

/**
 * Per-page-load dedupe. main.tsx wires BOTH window 'vite:preloadError' AND a
 * try/catch around the startup dynamic imports; a single failure can trigger both.
 * A page load resets JS module state, so this naturally starts false each load; the
 * cross-load count lives in sessionStorage. Reset on a confirmed-clean mount so a
 * later (post-mount) route-chunk failure in the same load can still act.
 */
let actedThisLoad = false;

/** Recovery reloads already issued this episode (0 if none / storage unavailable-safe). */
export function recoveryAttemptsUsed(): number {
  return readAttempts();
}

function readAttempts(): number {
  try {
    const raw = sessionStorage.getItem(ATTEMPTS_KEY);
    const n = raw == null ? 0 : parseInt(raw, 10);
    return Number.isInteger(n) && n >= 0 ? n : 0;
  } catch {
    // sessionStorage unavailable (private mode / disabled) — cannot bound the loop,
    // so report the cap to refuse further reloads: better to NOT loop than to storm.
    return MAX_ATTEMPTS;
  }
}

function writeAttempts(n: number): void {
  try {
    sessionStorage.setItem(ATTEMPTS_KEY, String(n));
  } catch {
    /* best-effort — the readAttempts() cap already fails safe when storage is off */
  }
}

/**
 * Clear the attempt counter after a confirmed-clean load so a FUTURE legit chunk
 * failure (a later deploy) can self-heal from scratch (Owner directive). Also resets
 * the per-load dedupe so a post-mount route-chunk failure this same load can act.
 * Called only once the app has mounted successfully.
 */
export function clearRecoveryFlag(): void {
  actedThisLoad = false;
  try {
    sessionStorage.removeItem(ATTEMPTS_KEY);
  } catch {
    /* best-effort no-op */
  }
}

async function unregisterServiceWorkers(): Promise<void> {
  try {
    if (typeof navigator !== "undefined" && navigator.serviceWorker) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    /* best-effort — reload anyway; a plain reload still often recovers */
  }
}

function scheduleReload(delayMs: number): void {
  if (typeof window === "undefined") return;
  if (delayMs <= 0) {
    window.location.reload();
    return;
  }
  window.setTimeout(() => window.location.reload(), delayMs);
}

/**
 * Self-heal on a chunk dynamic-import failure, bounded + edge-aware.
 *
 * Attempt escalation (per episode, counted in sessionStorage):
 *   attempts 0..N-1  → PLAIN reload after BACKOFF_MS[attempt] (do NOT unregister —
 *                      preserve precache so a converging edge window recovers without
 *                      widening edge exposure);
 *   attempt N        → last resort: unregister the SW, then reload (covers a genuine
 *                      SW-defect that a plain reload can't clear);
 *   attempt > N      → give up: return "give-up" without reloading, letting the
 *                      failure surface (an error boundary / broken page) instead of a
 *                      reload storm.
 *
 * Idempotent within a single page load (per-load dedupe): the first call schedules
 * recovery; subsequent calls this load return "already-acted".
 *
 * @returns the action taken (see SelfHealAction).
 */
export function selfHealChunkLoadFailure(): SelfHealAction {
  if (actedThisLoad) return "already-acted";

  const attempts = readAttempts();
  if (attempts >= MAX_ATTEMPTS) {
    // Bounded retries (plain backoff + last-resort unregister) all failed → surface.
    return "give-up";
  }

  actedThisLoad = true;
  writeAttempts(attempts + 1);

  if (attempts < BACKOFF_MS.length) {
    // Plain-reload retry — SW/precache preserved (edge-root-cause friendly).
    scheduleReload(BACKOFF_MS[attempts]!);
    return "retry";
  }

  // Last resort: the plain retries didn't help → unregister the SW (drop a
  // stale/defective precache) then reload once more.
  void unregisterServiceWorkers().finally(() => scheduleReload(0));
  return "unregister-reload";
}
